import React from 'react'
import styled from 'styled-components'
import { Table, useMediaSize, get } from '@ally/metronome-ui'
import {
  AccountType,
  Account,
  AccountStatus,
} from '@ally/data-types-afg-accounts'
import { sortBy } from 'lodash-es'
import { getColumns } from './TableColumns'
import { useCustomizeDashboard } from '../../CustomizeDashboard/CustomizeDashboardContext/CustomizeDashboardContext'
import MobileHomeLoansList from '../MobileAccountsCard/MobileHomeLoansList'
import { useAccountTypes } from '../../providers'
import {
  AccountCard,
  AccountCardMbox,
  AccountGroup,
  NonPrintingWrapper,
} from '../Shared'
import { useHostServices } from '../../HostServices'
import { isAccountBlocked } from '../../utils'

const mortgageStatusWeightLookup = (account: Account): number => {
  // keep this above the ACTIVE logic
  if (isAccountBlocked(account) || account.status === AccountStatus.PAIDOFF) {
    return 2
  }

  if (account.status === AccountStatus.ACTIVE) {
    return 1
  }

  return 3 // if somehow we haven't accounted for a status, then put it very last
}

/**
 * Using lodash, sorts accounts by status and then accountNumber.
 */
function sortByStatusAndAccountNumber(accounts: Account[]): Account[] {
  return sortBy(accounts, [
    (account): number => mortgageStatusWeightLookup(account),
    (account): number => parseInt(account.accountNumber, 10),
  ])
}

// Added margin-top 5px to create even space above and below the divider
const StyledDivider = styled.div`
  border-top: 1px solid ${get.colors('slate-2')};
  margin-top: 5px;
`

const Mortgage: React.FC = () => {
  const { redirectToMortgage } = useHostServices()
  const [{ dashboardView }] = useCustomizeDashboard()
  const { allMortgageAccounts } = useAccountTypes()

  const mortgageAccounts = allMortgageAccounts.filter(
    account => account.type === AccountType.MORTGAGE,
  )
  const helocAccounts = allMortgageAccounts.filter(
    account => account.type === AccountType.HOME_EQUITY,
  )

  const isMdDown = useMediaSize('MdDown')
  const isLgDown = useMediaSize('LgDown')
  const isPrint = window.matchMedia('print').matches

  const sortedMortgageAccounts = sortByStatusAndAccountNumber(mortgageAccounts)
  const sortedHelocAccounts = sortByStatusAndAccountNumber(helocAccounts)
  const allSortedHomeLoanAccounts = [
    ...sortedMortgageAccounts,
    ...sortedHelocAccounts,
  ]

  return (
    <>
      <AccountGroup name="home-loan">
        <AccountCard type="MORTGAGE">
          {isMdDown && !isPrint ? (
            <MobileHomeLoansList accounts={allSortedHomeLoanAccounts} />
          ) : (
            <>
              {sortedMortgageAccounts.length ? (
                <Table
                  data={sortedMortgageAccounts}
                  columns={getColumns({
                    tableTypeHeader: 'Mortgage',
                    view: dashboardView,
                    isLgDown,
                    allHomeLoanAccounts: allSortedHomeLoanAccounts,
                    redirectToMortgage,
                  })}
                  captionText="Information about your Ally mortgage accounts"
                  fullWidth
                  suppressMdDownLayout
                  withFooter={!helocAccounts.length}
                />
              ) : // istanbul ignore next
              null}

              {sortedHelocAccounts.length ? (
                <>
                  {sortedHelocAccounts.length && mortgageAccounts.length ? (
                    <StyledDivider />
                  ) : // istanbul ignore next
                  null}
                  <Table
                    data={sortedHelocAccounts}
                    columns={getColumns({
                      tableTypeHeader: 'Home Equity',
                      view: dashboardView,
                      isLgDown,
                      allHomeLoanAccounts: allSortedHomeLoanAccounts,
                      redirectToMortgage,
                    })}
                    captionText="Information about your Ally home equity lines of credit"
                    fullWidth
                    suppressMdDownLayout
                    withFooter
                  />
                </>
              ) : // istanbul ignore next
              null}
            </>
          )}
          <NonPrintingWrapper>
            <AccountCardMbox name="ally-dashboard-mortgage-well" />
          </NonPrintingWrapper>
        </AccountCard>
      </AccountGroup>
    </>
  )
}

export default Mortgage
